import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Download, RefreshCw, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getClosedDealsByProjectId
} from '../../../builder/services/api/implementation/impl';
import {
    downloadClosedDealInvoice
} from '../../services/api/implementation/impl';

import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Table from '../../../shared/components/layout/Table';
import Search from '../../../shared/components/ui/Search';
import { transformData } from '../../utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import UpdatePaymentStatusModal from '../forms/UpdatePaymentStatusModal';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';

// Helper to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const ProjectClosedDeals = () => {
    const { builderId, companyId, projectId } = useParams();
    const userId = Cookies.get('userId');
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [page, setPage] = useState(1);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedDealId, setSelectedDealId] = useState(null);
    const { register } = useForm();
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

    // Fetch closed deals for this project
    const { data: closedDealsData, isFetching: isFetchingDeals, refetch } = useQuery({
        queryKey: ['projectClosedDeals', projectId],
        queryFn: () => getClosedDealsByProjectId(projectId),
        onError: (err) => {
            console.error('Error fetching closed deals:', err);
            toast.error('Failed to fetch closed deals');
        }
    });

    const handleAction = async (e, type, id) => {
        e.stopPropagation();
        if (type === 'download') {
            try {
                toast.loading('Downloading invoice...', { id: 'download-invoice' });
                const blob = await downloadClosedDealInvoice(id);

                // Create object URL
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `invoice_${id}.pdf`); // Assuming PDF, adjust if needed

                // Append to body, click, and remove
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);

                toast.success('Invoice downloaded successfully', { id: 'download-invoice' });
            } catch (err) {
                console.error('Download error:', err);
                toast.error('Failed to download invoice', { id: 'download-invoice' });
            }
        } else if (type === 'update') {
            setSelectedDealId(id);
            setShowPaymentModal(true);
        }
    };

    // Process data for the table
    const processedData = useMemo(() => {
        if (!closedDealsData?.data) return [];

        return closedDealsData.data.map((deal) => {
            return {
                id: deal.id,
                customer_name: deal.lead?.user?.name || deal.lead?.reference_name || 'N/A',
                unit_name: deal.unit?.unitName || 'N/A',
                closed_date: formatDate(deal.closed_at),
                raw_date: deal.closed_at,
                status: deal.isPaymentCompleted ? 'Completed' : 'Pending',
                total_price: deal.totalPrice || deal.total_price || '0',
                action: (
                    <div className="flex gap-2 action-button">
                        <button
                            disabled={!deal.isPaymentCompleted}
                            className={`p-2 rounded-md transition-colors duration-200 ${!deal.isPaymentCompleted
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                                }`}
                            title={!deal.isPaymentCompleted ? "Invoice Pending" : "Download Invoice"}
                            onClick={(e) => deal.isPaymentCompleted && handleAction(e, 'download', deal.id)}
                        >
                            <FileText size={16} />
                        </button>
                        <button
                            disabled={deal.isPaymentCompleted}
                            className={`p-2 rounded-md transition-colors duration-200 ${deal.isPaymentCompleted
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                }`}
                            title={deal.isPaymentCompleted ? "Payment Completed" : "Update Payment Status"}
                            onClick={(e) => !deal.isPaymentCompleted && handleAction(e, 'update', deal.id)}
                        >
                            <RefreshCw size={16} />
                        </button>
                    </div>
                )
            };
        });
    }, [closedDealsData]);

    // Unified Filter Effect
    useEffect(() => {
        let data = processedData || [];

        // Month Filter
        if (selectedMonth) {
            data = data.filter(deal => {
                if (!deal.raw_date) return false;
                return new Date(deal.raw_date).getMonth() + 1 === parseInt(selectedMonth);
            });
        }

        // Search Filter
        if (searchQuery) {
            const lower = searchQuery.toLowerCase();
            data = data.filter(deal =>
                (deal.customer_name?.toLowerCase().includes(lower)) ||
                (deal.unit_name?.toLowerCase().includes(lower))
            );
        }

        setFilteredData(data);
    }, [processedData, searchQuery, selectedMonth]);

    // Transform for table consumption
    const tableDataFormat = ['id', 'customer_name', 'unit_name', 'closed_date', 'status', 'total_price', 'action'];
    const transformedData = filteredData.map((el) =>
        transformData(el, tableDataFormat)
    );

    const isLoading = isFetchingDeals;

    const handleRowClick = (dealId) => {
        console.log('Deal clicked:', dealId);
        // Future: Navigate to deal details?
    };

    return (
        <Layout>
            {/* Top Header */}
            <div className="fixed z-[20] top-0 left-[16.5vw] w-[83.5vw]">
                <Header data={{ name: 'Project Closed Deals' }} backArrow={true} />
            </div>

            {/* Search */}
            <div className="flex items-center gap-4 px-6 py-4 bg-white fixed top-[4rem] z-20 border-t-[2px] border-stone-100 w-[83vw] shadow-sm">
                <Search query={searchQuery} onChange={setSearchQuery} />
                <FormInput
                    type="date-month-superadmin"
                    register={register}
                    register_key="month"
                    valuePre={selectedMonth}
                    onType={(val) => setSelectedMonth(val.id)}
                    label=""
                    isRequired={false}
                />
                {/* <div className="w-[200px]">
                    <FormInput
                        type="date-month"
                        register={register}
                        register_key="month"
                        valuePre={selectedMonth}
                        onType={(val) => setSelectedMonth(val.id)}
                        label=""
                        isRequired={false}
                    />
                </div> */}
            </div>

            {/* Main Content - Table */}
            <GridWrapper width={100}>
                <div className="mt-[10rem] relative user-table-container h-[calc(100vh-11.5rem)] overflow-auto w-full">
                    <style>{`
              .user-table-container .table-body {
                position: static !important;
                overflow-y: visible !important;
                max-height: none !important;
                height: auto !important;
                top: auto !important;
                padding-bottom: 0 !important;
              }
              .user-table-container .table-container {
                height: auto !important;
              }
              .user-table-container > div {
                height: auto !important;
              }
            `}</style>

                    {isLoading ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <Table
                            headers={['S.No', 'Customer Name', 'Unit Name', 'Closed Date', 'Status', 'Total Price', 'Action']}
                            data={transformedData.map((item, index) => ({
                                serialNumber: index + 1,
                                ...item
                            }))}
                            identifier={'id'}
                            handleRowClick={handleRowClick}
                        />
                    )}
                </div>
            </GridWrapper>

            <UpdatePaymentStatusModal
                isOpen={showPaymentModal}
                onClose={() => {
                    setShowPaymentModal(false);
                    setSelectedDealId(null);
                }}
                dealId={selectedDealId}
                onSuccess={() => {
                    console.log('Payment updated');
                    refetch();
                }}
            />
        </Layout>
    );
};

export default ProjectClosedDeals;
